import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserInfo, type UserInfo } from '@/api/agent';

const NAV_ITEMS = [
  { path: '/admin', label: '数据概览', icon: 'ri-dashboard-3-line', exact: true },
  { path: '/admin/agents', label: '代理商管理', icon: 'ri-team-line' },
  { path: '/admin/materials', label: '资料管理', icon: 'ri-folder-upload-line' },
  { path: '/admin/watermark', label: '水印设置', icon: 'ri-shield-check-line' },
];

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export default function AdminLayout({ children, title, subtitle }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  useEffect(() => {
    getUserInfo()
      .then(setUserInfo)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
        setAdminMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (item: typeof NAV_ITEMS[0]) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-slate-900 flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-56'} lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-5 py-4 border-b border-slate-700/50 ${collapsed ? 'justify-center' : ''}`}>
          {collapsed ? (
            <img
              src="/images/logo-collapsed.png"
              alt="Sinovision"
              className="w-8 h-8 rounded-lg object-contain shrink-0"
            />
          ) : (
            <img
              src="/images/logo-full.png"
              alt="Sinovision"
              className="object-contain brightness-0 invert h-7 w-auto"
            />
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              className={`w-full flex items-center rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-3 py-2.5'} ${
                isActive(item)
                  ? 'text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              style={isActive(item) ? { background: '#0069BD' } : {}}
              title={collapsed ? item.label : undefined}
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-sm`}></i>
              </div>
              {!collapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Collapse Toggle Handle — attached to right edge */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="absolute top-1/2 -translate-y-1/2 -right-3 z-20 hidden lg:flex w-6 h-16 items-center justify-center rounded-r-md bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors cursor-pointer"
          style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.2)' }}
          title={collapsed ? '展开' : '收起'}
        >
          <i className={`text-sm ${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'}`}></i>
        </button>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-56'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-100 min-h-14 flex items-center px-4 md:px-6 py-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer shrink-0 mr-3"
          >
            <i className="ri-menu-line text-lg"></i>
          </button>

          {/* Title */}
          <div className="flex-1 min-w-0 mr-4 flex flex-col justify-center">
            <h1 className="text-base font-bold text-gray-900 leading-snug truncate">{title}</h1>
            {subtitle && <p className="text-xs text-gray-400 leading-snug truncate mt-0.5">{subtitle}</p>}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Manual Link */}
            <button
              onClick={() => navigate('/admin/manual')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${location.pathname === '/admin/manual' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              style={location.pathname === '/admin/manual' ? { background: '#0069BD' } : {}}
              title="用户操作手册"
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-sm"></i>
              </div>
              <span className="hidden md:block">用户手册</span>
            </button>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 hidden md:block" />

            {/* Admin Avatar with Dropdown */}
            <div className="relative" ref={adminMenuRef}>
              <button
                onClick={() => setAdminMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-gray-100 transition-all cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#0069BD' }}>
                  {userInfo?.name?.[0] || ''}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden md:block">{userInfo?.name || ''}</span>
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`ri-arrow-down-s-line text-sm text-gray-400 transition-transform duration-200 ${adminMenuOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {/* Dropdown */}
              {adminMenuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white border border-gray-100 rounded-xl overflow-hidden z-50" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-800">{userInfo?.name || ''}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{userInfo?.email || userInfo?.loginname || ''}</p>
                  </div>
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={() => { setAdminMenuOpen(false); localStorage.removeItem('agent_token'); navigate('/login'); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-logout-box-r-line text-sm"></i>
                    </div>
                    退出登录
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
